import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  FlowContentArea,
  FlowContextPane,
  FlowDetailedView,
  FlowStageCard,
  ResourceBar,
  GuestDetailsContainer,
  GuestProfileImage,
  GuestQuickDetails,
  StageDataField,
  StageTriggerSection,
  // useFlowUIStore,
  useFlowSearchParams,
  FLOW_GUEST_TABS,
  type FlowStageInfo,
} from '@Components/Flows';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useCallback, useState } from 'react';
import { ClipboardList, Mail, MessageCircle } from 'lucide-react';
import { useFlowInstance } from '@Hooks/useFlowInstance';
import { useFish } from '@Hooks/useFish';
import { useActivity } from '@Hooks/useActivity';
import {
  FishDetailsProvider,
  useFishDetails,
} from '../../fish-details/FishDetailsContext';
import FishDetailsOverlays from '../../fish-details/FishDetailsOverlays';
import type { FlowStageStatus } from '../../../components/Flows/types';
import { StageContentTypeform } from '../../../components/Flows/StageContentTypeform';
import { StageContentRegistration } from '../../../components/Flows/StageContentRegistration';
import { StageContentCustomForm } from '../../../components/Flows/StageContentCustomForm';
import { formatDate } from '@Utils/utils';
import { StageContentPDDeposit } from '@Components/Flows/StageContentPDDeposit';
import { StageContentTicketPurchase } from '@Components/Flows/StageContentTicketPurchase';
import { StageContentShopPurchase } from '@Components/Flows/StageContentShopPurchase';

function ProcessStages(stages: any[]) {
  const activeId =
    stages?.find((stage) => stage.result === null)?.id ??
    stages?.[stages.length - 1]?.id ??
    null;
  if (!activeId) return [];
  const activeIndex = stages?.findIndex((s) => s.id === activeId);

  const result = stages?.map((stage, index) => {
    let status = '';
    if (index < activeIndex) {
      status = 'previous';
    } else if (index === activeIndex) {
      status = 'active';
    } else if (index === activeIndex + 1) {
      status = 'next';
    } else {
      status = 'upcoming';
    }

    return {
      id: stage?.id,
      name: stage?.title,
      status: status as FlowStageStatus,
      icon: <ClipboardList className="size-5" />,
    };
  });
  return result;
}

function ProcessTriggers(triggers: any[]) {
  if (!triggers) return [];

  const result = triggers.map((trigger) => {
    let timestamp = 'N/A';
    if (trigger.triggerReceipts.length > 1) {
      const time = trigger.triggerReceipts.reduce((latest, current) => {
        const latestDate = new Date(latest.createdAt).getTime();
        const currentDate = new Date(current.createdAt).getTime();

        return currentDate > latestDate ? current : latest;
      }).createdAt;

      timestamp = formatDate(time);
    } else if (trigger.triggerReceipts.length === 1) {
      timestamp = formatDate(trigger.triggerReceipts[0].createdAt);
    }
    return {
      type: trigger.actions?.type,
      message: trigger.title,
      timestamp: timestamp,
    };
  });

  return result;
}

function checkMembership(fish) {
  let isActiveMember = false;
  if (fish?.subscriptions?.length) {
    for (const sub of fish.subscriptions) {
      if (sub.cancelledAt === null && sub.deletedAt === null) {
        isActiveMember = true;
      }
    }
  }
  return isActiveMember;
}

function processStageContent(allStages, stage, flowInstanceId, fish) {
  switch (stage?.flowStageTypes?.name) {
    case 'TYPEFORM':
      return <StageContentTypeform formData={(stage as any)?.form?.qa} />;

    case 'APPLICATION_REVIEW':
    case 'CUSTOM_FORM': {
      const customFormId =
        stage?.dependencyIdentifiers?.length === 2
          ? stage?.dependencyIdentifiers[1]
          : stage?.dependencyIdentifiers[0];

      const hydrateData = stage?.form?.responses?.[0];

      const stageIndex = allStages.findIndex((s) => s.id === stage.id);
      let prevResponseId = null;
      if (stageIndex > 0) {
        prevResponseId = allStages?.[stageIndex - 1]?.form?.responses?.[0]?.id;
      }

      const questionOrder = stage?.form?.uiSchemaJson?.['ui:order'];

      return (
        <StageContentCustomForm
          selectedFlowStageId={stage?.id}
          prevResponseId={prevResponseId}
          flowInstanceId={flowInstanceId}
          fishId={fish?.id}
          formDataHydrate={hydrateData ?? null}
          customFormId={customFormId}
          questionOrder={questionOrder}
        />
      );
    }

    case 'REGISTRATION_COMPLETION':
      return <StageContentRegistration fish={fish} />;

    case 'TICKETED_EVENT_TICKET_PURCHASE_COMPLETION':
      return <StageContentTicketPurchase stage={stage} />;

    case 'PRIVATE_DINING_DEPOSIT_COMPLETION':
      return <StageContentPDDeposit stage={stage} />;

    case 'SHOP_PURCHASE_COMPLETION':
      return <StageContentShopPurchase stage={stage} />;
  }
}

function FlowGuestDetailsBar({
  isExpanded,
  onToggle,
  guestTab,
  setGuestTab,
  avatarId,
  fullName,
  phone,
  email,
  allTimeSpend,
  isMember,
  spendSummary,
}) {
  const { setModalContent, openModal } = useFishDetails();

  return (
    <>
      <div className="fixed bottom-0 left-0 z-10 w-full">
        <ResourceBar
          isDetailsExpanded={isExpanded}
          onToggleDetails={onToggle}
          actions={
            <>
              {phone && (
                <div className="flex items-center gap-2 rounded-xl bg-zinc-900 px-3 py-2">
                  <span className="text-sm leading-4 font-medium text-white">
                    {phone}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setModalContent('sms');
                      openModal();
                    }}
                    className="border-input gap-2 rounded-md bg-white/5 shadow-xs"
                  >
                    <MessageCircle className="size-4" />
                    SMS
                  </Button>
                </div>
              )}
              {email && (
                <div className="flex items-center gap-2 rounded-xl bg-zinc-900 px-3 py-2">
                  <span className="break-word text-sm leading-4 font-medium text-white">
                    {email}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setModalContent('email');
                      openModal();
                    }}
                    className="border-input gap-2 rounded-md bg-white/5 shadow-xs"
                  >
                    <Mail className="size-4" />
                    Email
                  </Button>
                </div>
              )}
            </>
          }
          detailsContent={
            <GuestDetailsContainer
              tabs={FLOW_GUEST_TABS}
              activeTab={guestTab}
              onTabChange={setGuestTab}
              spendSummary={spendSummary}
              // Fixed height so switching tabs doesn't resize the pane to each tab's content.
              panelClassName="dark h-[60vh] overflow-y-auto text-zinc-100"
            />
          }
        >
          <GuestProfileImage
            cloudinaryPublicId={avatarId}
            imageUrl={null}
            alt={fullName}
            className="absolute size-32"
          />
          <GuestQuickDetails
            fullName={fullName}
            totalSpend={allTimeSpend}
            isClubMember={isMember}
            className="pl-36"
          />
        </ResourceBar>
      </div>
      <FishDetailsOverlays />
    </>
  );
}

export default function FlowSamplePage() {
  const navigate = useNavigate();
  const { stage, setStage, view, setView, guestTab, setGuestTab } =
    useFlowSearchParams();
  const [isGuestDetailsExpanded, setIsGuestDetailsExpanded] = useState(false);
  const { flowId } = useParams();

  const { data: instancePayload } = useFlowInstance({
    flowInstanceId: flowId,
    enabled: true,
  });

  const { data: fish } = useFish({ fishId: instancePayload?.fishId });

  const fishFullName = `${fish?.preferredProfile?.fName ?? '-'} ${fish?.preferredProfile?.lName ?? '-'}`;
  const fishIsMember = checkMembership(fish);

  const allStages = instancePayload?.flow?.flowStages;


  // Activity hook with showCancelled toggle
  const { activity } = useActivity({ fishId: fish?.id, enabled: !!fish?.id });

  const mtdSpend = activity?.meta?.mtdTotal
    ? parseFloat(activity?.meta?.mtdTotal as any).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '$0.00';

  const ytdSpend = activity?.meta?.ytdTotal
    ? parseFloat(activity?.meta?.ytdTotal as any).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '$0.00';

  const allTimeSpend = activity?.meta?.allTotal
    ? parseFloat(activity?.meta?.allTotal as any).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '$0.00';

  const stagesInfo = ProcessStages(allStages);

  const defaultStageId =
    allStages?.find((stage: any) => stage.result === null)?.id ??
    allStages?.[allStages?.length - 1].id;

  const activeStageId = stage ?? defaultStageId;

  const activeStageIndex = useMemo(
    () =>
      Math.max(
        0,
        allStages?.findIndex((s) => s.id === activeStageId),
      ),
    [activeStageId],
  );

  const activeStage: FlowStageInfo = stagesInfo
    ? stagesInfo[activeStageIndex]
    : stagesInfo[stagesInfo.length - 1];

  const fullActiveStage: any = allStages?.find((s) => s.id === activeStageId);

  const isFirstStage = activeStageIndex === 0;
  const isLastStage = activeStageIndex === allStages?.length - 1;

  const handleStageSelect = useCallback(
    (id: string) => setStage(id),
    [setStage],
  );

  const handleStageNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      const nextIndex =
        direction === 'prev' ? activeStageIndex - 1 : activeStageIndex + 1;
      const clamped = Math.max(0, Math.min(nextIndex, stagesInfo?.length - 1));
      setStage(stagesInfo[clamped].id);
    },
    [activeStageIndex, setStage],
  );


  return (
    <div className="flex h-screen flex-col overflow-y-auto pb-36">
      <FlowContextPane
        flowTitle={instancePayload?.flow?.flowTitle}
        startTime={formatDate(instancePayload?.createdAt)}
        stages={stagesInfo ?? []}
        activeStageId={activeStageId}
        stageTitle={activeStage?.name}
        stageStatus={activeStage?.status}
        activeView={view}
        onViewChange={setView}
        onClose={() => {
          setIsGuestDetailsExpanded(false);
          navigate(-1);
        }}
        onStageSelect={handleStageSelect}
        onStageNavigate={handleStageNavigate}
        disablePrev={isFirstStage}
        disableNext={isLastStage}
      />

      {view === 'flow' ? (
        <FlowContentArea activeIndex={activeStageIndex}>
          {allStages?.map((stage, i) => {
            return (
              <FlowStageCard
                key={`${stage.id}-${i}`}
                title={stage?.title}
                icon={<ClipboardList className="size-5" />}
                status={stagesInfo[i]?.status}
                focused={activeStageIndex === 0}
                triggerSection={
                  <StageTriggerSection
                    triggers={ProcessTriggers(stage.flowStageTriggers) as any}
                  />
                }
              >
                {processStageContent(
                  allStages,
                  stage,
                  instancePayload?.id,
                  fish,
                )}
              </FlowStageCard>
            );
          })}
        </FlowContentArea>
      ) : (
        <FlowDetailedView stages={stagesInfo ?? []}>
          <div className="flex flex-col gap-6">
            <div className="rounded-xl border border-zinc-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                {/* <Phone className="size-5" /> */}
                <h2 className="text-xl font-bold">{activeStage?.name}</h2>
                <span className="text-red-500">*</span>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <StageDataField label="Guest" value={fishFullName} />
                <StageDataField
                  label="Completed On"
                  value={formatDate(fullActiveStage?.result?.createdAt)}
                />
              </div>

              <div className="my-4 h-px w-full bg-zinc-200" />

              <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4">
                {processStageContent(
                  allStages,
                  fullActiveStage,
                  instancePayload?.id,
                  fish,
                )}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white p-6">
              <h3 className="mb-3 text-sm font-semibold tracking-wide text-zinc-500 uppercase">
                Triggers
              </h3>
              <div className="flex flex-col gap-2">
                {[...ProcessTriggers(fullActiveStage?.flowStageTriggers)].map(
                  (trigger, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3"
                    >
                      <div>
                        <span className="text-sm font-semibold text-zinc-600">
                          {trigger.type}
                        </span>
                        <span className="mx-2 text-zinc-300">·</span>
                        <span className="text-sm text-zinc-700">
                          {trigger.message}
                        </span>
                      </div>
                      <span className="text-xs text-zinc-400">
                        {trigger.timestamp}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>

          </div>
        </FlowDetailedView>
      )}

      {instancePayload?.fishId && (
        <FishDetailsProvider fishId={instancePayload.fishId} variant="guest">
          <FlowGuestDetailsBar
            isExpanded={isGuestDetailsExpanded}
            onToggle={() => setIsGuestDetailsExpanded(!isGuestDetailsExpanded)}
            guestTab={guestTab}
            setGuestTab={setGuestTab}
            avatarId={fish?.mostRecentAsset?.cloudinaryId}
            fullName={fishFullName}
            phone={fish?.phoneNumber}
            email={fish?.preferredProfile?.email}
            allTimeSpend={allTimeSpend}
            isMember={fishIsMember}
            spendSummary={{ mtdSpend, ytdSpend, allTimeSpend }}
          />
        </FishDetailsProvider>
      )}
    </div>
  );
}
